import dayjs from "dayjs";
import { Button, Col, Collapse, Dropdown, Empty, Form, Select, Input, Row, Spin, Typography, Switch, Tooltip, Popover } from "antd";
import { useForm } from "antd/lib/form/Form";
import { Link } from "react-router-dom";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import React, { useEffect, useState } from "react";

import Formatacoes from "@/Utils/Formatacao";
import { RelatorioService, useGerarRelatorioDeVisaoGeralDeMovimentacoes_ComPaginacao } from "@/Services/Financeiro/Titulo/Relatorios/RelatoriosService";

import { Resultado } from "@/Models/Financeiro/Titulo/Relatorios/RelatorioDeVisaoGeralDeMovimentacoes";
import { PaginaInfo } from "@/Models/Utils/Pagina/Pagina";
import { EnumPessoaTipo } from "@/Models/Pessoa/Pessoa";

import { ColumnWithBreakpoints } from "@/Components/ResponsiveAntdTable";
import InputRangePicker from "@/Components/Base/Input/InputRangePicker";
import ViagemSelect from "@/Components/Populados/Viagem/ViagemSelect";
import { PessoaSelect } from "@/Components/Populados/Pessoa/PessoaSelect";
import TablePaginadaBasic, { ITablePaginadaBasicProps } from "@/Components/Base/Table/TablePaginadaBasic";

import TituloSituacaoSelect from "../../TituloSituacao/TituloSituacaoSelect";
import PlanoDeContasSelect from "../../../PlanoDeContas/PlanoDeContasSelect";
import CentroCustoSelect from "../../../CentroConta/CentroCustoSelect";
import ContaSelect from "../../../Conta/ContaSelect";
import { TypeCentroCusto } from "@/Models/Financeiro/CentroCusto";
import { TypePlanoContas } from "@/Models/Financeiro/PlanoContas";
import { Balanco } from "@/Components/Balanco";
import Conjunto from "@/Components/Conjunto";
import Utilitario from "@/Utils/Utilitario";
import { Movimentacao } from "@/Models/Financeiro/Conta/Movimentacao";
import MovimentacaoDesativarPopconfirm from "../../../Conta/Movimentacao/MovimentacaoDesativarPopconfirm";
import PagamentoTipoSelect from "../../Pagamento/PagamentoTipo/PagamentoTipoSelect";
import OpceosTitulo from "../../OpceosTitulo";
import Titulo, { EnumTituloTipo } from "@/Models/Financeiro/Titulo/Titulo";
import { EnumTituloSituacao } from "@/Models/Financeiro/Titulo/TituloSituacao";
import { EditOutlined, SettingOutlined } from "@ant-design/icons";
import { EnumTipoConta } from "@/Models/Financeiro/Conta";
import ComponentePermissao from "@/Components/Base/Permissao/ComponentePermissao";
import { AlterarStatus } from "@/Views/Viagem/Cadastros/Viagem/Inscricoes";
import { StatusDaInscricaoDoAlunoNaViagemDicionario } from "@/Models/Domain/Viagem/Enum";

const { Paragraph, Text } = Typography;

type Parametros = Parameters<typeof useGerarRelatorioDeVisaoGeralDeMovimentacoes_ComPaginacao>[1];
type Filtro = Parametros["filtro"] & {
    centroDeCustos?: TypeCentroCusto;
    planoDeContas?: TypePlanoContas;
    _intervaloDeEmissao?: [Date, Date];
    _intervaloDeVencimento?: [Date, Date];
    _intervaloDeLiquidacao?: [Date, Date];
};

const FiltroComponent: React.FC<{}> = () => {
    const form = useFormInstance<Filtro>();

    if (form == undefined) throw new Error("O componente deve estar aninhado em um formulário");

    const codCentroCustoForm = Form.useWatch<TypeCentroCusto|null>(["centroDeCustos"], form);

    return (
        <React.Fragment>
            <Collapse
                size="small"
                ghost
                defaultActiveKey={["filtros"]}
                items={[{ key: 'filtros', label: 'Filtros', destroyInactivePanel : false, forceRender : true, children: <>
                    <React.Fragment>
                        <Row gutter={3} style={{ alignItems: "end" }}>
                            <Col xs={12} sm={6} md={6} lg={3} xl={3} xxl={3}>
                                <Form.Item<Filtro> label="Tipo" name="tipoDeMovimentacao" rules={[{ required: true }]} initialValue={"todos"}>
                                    <Select
                                        placeholder={"Selecione"}
                                        options={[
                                            {
                                                label: "Todos",
                                                value: "todos",
                                            },
                                            {
                                                label: "Receitas",
                                                value: "entradas",
                                            },
                                            {
                                                label: "Despesas",
                                                value: "saidas",
                                            },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={12} sm={6} md={6} lg={3} xl={3} xxl={3}>
                                <Form.Item<Filtro>
                                    label="Status"
                                    name="codSituacao"
                                    rules={[{ required: false, message: "Selecione a situação" }]}
                                >
                                    <TituloSituacaoSelect />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                <Form.Item<Filtro>
                                    label="Emissão"
                                    name={"_intervaloDeEmissao"}
                                    rules={[{ required: false, message: "Selecione o intervalo da busca" }]}
                                >
                                    <InputRangePicker
                                        picker="date"
                                        disabledDate={(dia, v) => {
                                            return false;
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                <Form.Item<Filtro>
                                    label="Vencimento"
                                    name={"_intervaloDeVencimento"}
                                    // initialValue={[dayjs().startOf("month"), dayjs().endOf("month")]}
                                    rules={[{ required: false, message: "Selecione o intervalo da busca" }]}
                                >
                                    <InputRangePicker
                                        allowClear
                                        picker="date"
                                        disabledDate={(dia, v) => {
                                            return false;
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
                                <Form.Item<Filtro>
                                    label="Liquidação"
                                    name={"_intervaloDeLiquidacao"}
                                    rules={[{ required: false, message: "Selecione o intervalo da busca" }]}
                                >
                                    <InputRangePicker
                                        allowClear
                                        picker="date"
                                        disabledDate={(dia, v) => {
                                            return false;
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                        </Row>
                    </React.Fragment>
                    <React.Fragment>
                        <Row gutter={3} style={{ alignItems: "end" }}>
                            <Col xs={24} md={12} lg={5}>
                                <Form.Item<Filtro> name="codFornecedor" label="Empresa" rules={[{ required: false, message: "Selecione o fornecedor" }]}>
                                    <PessoaSelect
                                        style={{ marginBottom: 0 }}
                                        tipoPessoa={EnumPessoaTipo.FORNECEDOR}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={5}>
                                <Form.Item<Filtro>
                                    label="Responsável"
                                    name="codResponsavel"
                                    rules={[{ required: false, message: "Selecione o responsável" }]}
                                >
                                    <PessoaSelect tipoPessoa={EnumPessoaTipo.RESPONSAVEL} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12} lg={5}>
                                <Form.Item<Filtro> label="Aluno" name="codAluno" rules={[{ required: false, message: "Selecione o aluno" }]}>
                                    <PessoaSelect tipoPessoa={EnumPessoaTipo.ALUNO} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={5}>
                                <Form.Item<Filtro> label="Escola" name="codEscola" rules={[{ required: false, message: "Selecione a escola" }]}>
                                    <PessoaSelect tipoPessoa={EnumPessoaTipo.ESCOLA} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={4}>
                                <Form.Item<Filtro> label="Viagem" name="codViagem" rules={[{ required: false, message: "Selecione a viagem" }]}>
                                    <ViagemSelect placeholder="Selecione" style={{ whiteSpace: "break-spaces" }} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </React.Fragment>         
                    <React.Fragment>
                        <Row gutter={3} style={{ alignItems: "end" }}>
                            <Col xs={24} sm={12} md={12} lg={4} xl={4} xxl={4}>
                                <Form.Item<Filtro>
                                    label="C.C."
                                    name="centroDeCustos"
                                    rules={[{ required: false, message: "Selecione o centro de custos" }]}
                                >
                                    <CentroCustoSelect style={{ whiteSpace: "break-spaces" }} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={12} lg={4} xl={4} xxl={4}>
                                <Form.Item<Filtro>
                                    label="P.C."
                                    name="planoDeContas"
                                    rules={[{ required: false, message: "Selecione o plano de contas" }]}
                                >
                                    <PlanoDeContasSelect />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={12} lg={3} xl={3} xxl={3}>
                                <Form.Item<Filtro> label="Conta" name="codConta" rules={[{ required: false, message: "Selecione a conta" }]}>
                                    <ContaSelect 
                                        filtros={{
                                            codCentrosDeCustosADV : codCentroCustoForm?.codigo === undefined ? undefined : [codCentroCustoForm?.codigo],
                                            codTiposInvalidos : codCentroCustoForm?.codigo === undefined ? [EnumTipoConta.ADIANTAMENTO_VIAGEM] : undefined
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={12} lg={2} xl={2} xxl={2}>
                                <Form.Item<Filtro> label="Tp. Pgto" name="codTipoPagamento">
                                    <PagamentoTipoSelect />
                                </Form.Item>
                            </Col>
                            
                            <Col xs={24} sm={12} md={12} lg={3} xl={3} xxl={3}>
                                <Form.Item<Filtro> label="NSU" name="NSU" rules={[{ required: false, message: "Digite o NSU" }]}>
                                    <Input placeholder="NSU" addonBefore={<Tooltip title="Apenas registros com NSU">
                                            <Form.Item<Filtro> noStyle tooltip="Apenas registros com NSU" name="apenasComNsu" initialValue={false}>
                                                <Switch 
                                                    checkedChildren=""
                                                    unCheckedChildren=""
                                                    size="small"
                                                />
                                            </Form.Item>
                                        </Tooltip>} 
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={12} lg={2} xl={2} xxl={2}>
                                <Form.Item<Filtro> label="Documento" name="documento" rules={[{ required: false, message: "Digite o documento" }]}>
                                    <Input placeholder="Documento" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={12} lg={3} xl={3} xxl={3}>
                                <Form.Item<Filtro>
                                    label="N. Bancário"
                                    name="numeroBancario"
                                    rules={[{ required: false, message: "Digite o N. Bancário" }]}
                                >
                                    <Input placeholder="N. Bancário" addonBefore={<Tooltip title="Apenas registros com N. Bancário">
                                            <Form.Item<Filtro> noStyle name="apenasComNossoNumeroRetorno" initialValue={false}>
                                                <Switch 
                                                    checkedChildren=""
                                                    unCheckedChildren=""
                                                    size="small"
                                                />
                                            </Form.Item>
                                        </Tooltip>}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={12} lg={3} xl={3} xxl={3}>
                                <Form.Item<Filtro>
                                    label="Identif. Pago"
                                    name="identificadorDoPagamento"
                                    rules={[{ required: false, message: "Digite o identificador do pagamento" }]}
                                >
                                    <Input placeholder="Identif. Pago" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </React.Fragment>
                </> }]}
            />
            
        </React.Fragment>
    );
};

const TabelaDaVisaoGeralDeMovimentacoes: React.FC<{
    tableProps?: ITablePaginadaBasicProps<Resultado>;
    acquireCSVSource?: () => Promise<Resultado[]>;
    _plus?: {
        depoisDeDesativar?: () => any
    }
}> = (props) => {

    // const { mutate: desativar } = useDesativar({
    //     onSuccess() {
    //         props._plus?.depoisDeDesativar?.();
    //     },
    // });

    return (
        <div style={{
            // maxHeight : "70vh",
            // overflowY : "auto"
        }}>
            <div style={{
                // transform : "rotateX(180deg)",
                // direction : "ltr",
                // maxWidth : "max-content",
                // overflowX : "auto"
            }}>
                <TablePaginadaBasic<Resultado>
                    {...props.tableProps}
                    showHeader={true}
                    disableResponsiveness={true}
                    minColWidthIfResponsivenessDisabled={"80px"}
                    scroll={{
                        x : undefined,
                        y : "70vh"
                    }}
                    // style={{
                    //     transform : "rotateX(180deg)",
                    //     direction : "ltr"
                    // }}
                    // pagination={{
                    //     position: ["bottomRight", "topRight"]
                    // }}
                    size="small"
                    rowKey={(r) => r.codMovimentacao!}
                    tableLayout="auto"
                    columns={
                        [
                            {
                                title: "Escola",
                                ellipsis: {
                                    showTitle: false,
                                },
                                key: "escola",
                                render: (_, record) => (
                                        <Link to={`/cadastros/empresas/escola/editar?id=${record.codEscola}`} target="_blank">
                                            {record.escola?.nomeExibicao}
                                        </Link>
                                ),
                            },
                            {
                                breakpoints: {
                                    lg: 3,
                                },
                                title: "Viagem",
                                key: "viagem",
                                ellipsis: {
                                    showTitle: false,
                                },
                                render: (_, record) => (
                                    <Button type="link" size={"small"} style={{ textAlign : "left" }} href={`/viagens/viagens/detalhes?id=${record.codViagem}`} target="_blank">
                                        {record.viagem?.titulo}
                                    </Button>
                                ),
                            },
                            {
                                title: "Centro de custo",
                                key: "centro-custo",
                                ellipsis: {
                                    showTitle: false,
                                },
                                render: (_, record) => (
                                    <Link to={`/cadastros/contabilidade/centro-de-custo/editar?id=${record.centroCusto?.codigo}`} target="_blank">
                                        {record.centroCusto?.descricao}
                                    </Link>
                                ),
                            },
                            {
                                title: "Responsável",
                                key: "responsavel",
                                ellipsis: {
                                    showTitle: false,
                                },
                                render: (_, record) => <Link to={`/viagens/responsaveis/editar?id=${record.responsavel?.codigo}`} target="_blank">
                                    {record.responsavel?.nomeExibicao}
                                </Link>,
                            },
                            {
                                title: "Aluno",
                                key: "aluno",
                                ellipsis: {
                                    showTitle: false,
                                },
                                 csv: {
                                    title: "Aluno",
                                    get: (record) => record?.aluno?.nomeExibicao == undefined ? undefined : record?.aluno?.nomeExibicao
                                },
                                render: (_, record) => (
                                    <div style={{ display: "flex", justifyContent : "space-between" }}>
                                        <Link to={`/viagens/alunos/editar?id=${record.codAluno}`} title={record.aluno?.nomeExibicao} target="_blank">
                                            {record.aluno?.nomeExibicao}
                                        </Link>
                                        {
                                            (record.codViagem && record.codAluno && record.inscricao?.codigo && record.inscricao?.status) && <ComponentePermissao permissoes={["Viagem_Inscricao_AlterarStatus"]}>
                                                <Popover
                                                    destroyTooltipOnHide
                                                    trigger="click"
                                                    content={
                                                        <AlterarStatus
                                                            codViagem={record.inscricao.codViagem as number}
                                                            codInscricao={record.inscricao.codigo}
                                                            statusInicial={record.inscricao.status}
                                                            onSuccess={() => props?._plus?.depoisDeDesativar?.()}
                                                        />
                                                    }
                                                    placement="topLeft"                                                                    
                                                >
                                                    <Button type="dashed" size="small" icon={<EditOutlined />} title="Alterar Status da Inscrição" >
                                                        {StatusDaInscricaoDoAlunoNaViagemDicionario[record.inscricao.status]}
                                                    </Button>
                                                </Popover>
                                            </ComponentePermissao>
                                        }
                                        
                                    </div>
                                ),
                            },
                            {
                                title: "Emissão",
                                key: "dt-emissao",
                                render: (_, record) => Formatacoes.paraData(record.dataDeEmissao)?.toLocaleDateString(),
                            },
                            {
                                title: "Vencimento",
                                key: "dt-vencimento",
                                render: (_, record) => Formatacoes.paraData(record.dataDeVencimento)?.toLocaleDateString(),
                            },
                            {
                                title: "Liquidação",
                                key: "dt-liquidacao",
                                render: (_, record) => Formatacoes.paraData(record.dataDeLiquidacao)?.toLocaleDateString(),
                            },
                            {
                                title: "Base(R$)",
                                key: "vl-base",
                                render: (_, record) => {
                                    var v = record.valorBase == undefined ? undefined : !record.entrada ? -1 * record.valorBase : record.valorBase;
                                    return <Balanco>{Formatacoes.formatarReal(v)} </Balanco>;
                                },
                                csv: {
                                    title: "Base (R$)",
                                    get: (record) =>
                                        record?.valorBase == undefined ? undefined : Utilitario.toCSVDinheiro(!record.entrada ? -1 * record.valorBase : record.valorBase),
                                },
                            },
                            {
                                title: "Previsto(R$)",
                                key: "vl-previsto",
                                render: (_, record) => {
                                    var v = record.valorPrevisto == undefined ? undefined : !record.entrada ? -1 * record.valorPrevisto : record.valorPrevisto;
                                    return <Balanco>{Formatacoes.formatarReal(v)} </Balanco>;
                                },
                                csv: {
                                    title: "Previsto (R$)",
                                    get: (record) =>
                                        record?.valorPrevisto == undefined ? undefined : Utilitario.toCSVDinheiro(!record.entrada ? -1 * record.valorPrevisto : record.valorPrevisto),
                                },
                            },
                            {
                                title: "Multa/Juros",
                                key: "multa-juros",
                                minWidth: "100px",
                                render: (_, record) => {
                                    var v = record.valorMultaEJuros == undefined ? undefined : !record.entrada ? -1 * record.valorMultaEJuros : record.valorMultaEJuros;
                                    return <Balanco>{Formatacoes.formatarReal(v)} </Balanco>;
                                },
                                csv: {
                                    title: "Multa/Juros (R$)",
                                    get: (record) =>
                                        record?.valorMultaEJuros == undefined ? undefined : Utilitario.toCSVDinheiro(!record.entrada ? -1 * record.valorMultaEJuros : record.valorMultaEJuros),
                                },
                            },
                            {
                                title: "Taxa (R$)",
                                key: "vl-taxa",
                                minWidth: "100px",
                                render: (_, record) => (record.valorDaTaxa ?? 0) !== 0 ? <Balanco>{Formatacoes.formatarReal(record.valorDaTaxa ?? 0)}</Balanco> : "-",
                                csv: {
                                    title: "Taxa (R$)",
                                    get: (record) => Utilitario.toCSVDinheiro(record?.valorDaTaxa)
                                },
                            },
                            {
                                title: "Crédito (R$)",
                                key: "vl-credito",
                                minWidth: "100px",
                                render: (_, record) => {
                                    var v = record.valorDeCredito == undefined ? undefined : !record.entrada ? -1 * record.valorDeCredito : record.valorDeCredito;
                                    return <Balanco>{Formatacoes.formatarReal(v)} </Balanco>;
                                },
                                csv: {
                                    title: "Crédito (R$)",
                                    get: (record) =>
                                        record?.valorDeCredito == undefined ? undefined : Utilitario.toCSVDinheiro(!record.entrada ? -1 * record.valorDeCredito : record.valorDeCredito),
                                },
                            },
                            {
                                title: "NSU",
                                key: "nsu",
                                minWidth: "80px",
                                render: (_, record) => record.nsu,
                            },
                            {
                                title: "Documento",
                                key: "documento",
                                minWidth: "80px",
                                render: (_, record) => record.documento,
                            },
                            {
                                title: "N. bancário",
                                key: "numero-bancario",
                                render: (_, record) => record.numeroBancario,
                            },
                            {
                                title: "ID.Pagamento",
                                key: "id-pagamento",
                                render: (_, record) => record.identificadorDoPagamento,
                            },
                            {
                                minWidth: "100px",
                                title: "Agente",
                                key: "agente",
                                ellipsis: {
                                    showTitle: false,
                                },
                                render: (_, record) => record.fornecedor?.nomeExibicao,
                            },
                            {
                                title: "Forma",
                                key: "forma-pagamento",
                                minWidth: "80px",
                                render: (_, record) => record.movFormaPagamento?.nome,
                            },
                            {
                                title: "Conta",
                                minWidth: "100px",
                                key: "conta-e-banco",
                                ellipsis: {
                                    showTitle: false,
                                },
                                render: (_, record) => (
                                    <Link to={`/cadastros/bancos/conta/editar?id=${record.conta?.codigo}`} target="_blank">
                                        {record.conta?.nome}
                                    </Link>
                                ),
                            },
                            {
                                title: "PL. Contas",
                                minWidth: "100px",
                                ellipsis: {
                                    showTitle: false,
                                },
                                key: "pl-contas",
                                render: (_, record) => (
                                    <Link to={`/cadastros/contabilidade/plano-de-contas/editar?id=${record.planoDeContas?.codigo}`} target="_blank">
                                        {record.planoDeContas?.descricao}
                                    </Link>
                                )
                            },
                            {
                                title: "Situação",
                                ellipsis: {
                                    showTitle: false,
                                },
                                key: "situacao",
                                render: (_, record) => record.situacao?.nome,
                            },
                            {
                                title: "Telefones",
                                ellipsis: {
                                    showTitle: false,
                                },
                                key: "telefones",
                                render: (_, record) => {
                                    const telefone = record.fornecedor?.telefones?.filter(f => f.ativo === true)?.sort((a, b) => Number(b?.principal ?? false) - Number(a?.principal ?? false))?.[0];
                                    
                                    return telefone ? <Button href={`https://wa.me/55${telefone.ddd?.replace(/\D/g, '')}${telefone.numero?.replace(/\D/g, '')}?text=Parcela ${record.tituloNomeExibicao}`} size="small" target="_blank" type="link">({telefone.ddd}) {telefone.numero} <Text style={{ marginLeft : 10 }} copyable={{ text: `(${telefone.ddd}) ${telefone.numero}` }} /></Button> : undefined;
                                },
                            },
                            {
                                ellipsis: {
                                    showTitle: false,
                                },
                                title: "E-mails",
                                key: "emails",
                                render: (_, record) => {
                                    const email = record.fornecedor?.emails?.filter(f => f.ativo === true)?.sort((a, b) => Number(b?.principal ?? false) - Number(a?.principal ?? false))?.[0];
                                    
                                    return email ? <>
                                        <Button href={`mailto:${email.endereco}?subject=Parcela ${record.tituloNomeExibicao}`} size="small" target="_blank" type="link">{email.endereco} <Text style={{ marginLeft : 10 }} copyable={{ text: email.endereco }} /></Button>
                                    </> : undefined;
                                },
                            },
                            {
                                title: "Ações",
                                key: "acoes",
                                csv: { ignore: true },
                                fixed: "right",
                                render: (_, record) => {
                                    return (
                                        <OpceosTitulo 
                                            titulo={{
                                                codigo : record.codTitulo as number,
                                                codFatura : record.codFatura as number,
                                                codSituacao : (record.situacao?.codigo ?? EnumTituloSituacao.Pendente) as number,
                                                codMovimentacao : record.codMovimentacao as number,
                                                codTipo : EnumTituloTipo.Parcela,
                                                ativo : true
                                            } as Titulo}
                                            isDesativar={true}
                                            isAtivar={true}
                                            isPagar={true}
                                            isVisualizar={true}   
                                            isFormasPagamento={false}
                                            onSucess={() => props?._plus?.depoisDeDesativar?.()}                                         
                                        >
                                            <SettingOutlined />
                                        </OpceosTitulo>
                                    );
                                },
                            }
                        ] as ColumnWithBreakpoints<Resultado>[]
                    }
                    csv={{
                        enabled: props.acquireCSVSource != undefined,
                        acquireSource: async () => {
                            return props.acquireCSVSource?.() ?? [];
                        },
                    }}
                />
            </div>
        </div>
    );
}

export const RelatorioDaVisaoGeralDeMovimentacoes: React.FC<{}> = () => {
    const [formularioDeFiltro] = useForm<Filtro>();
    const [parametros, setParametros] = useState<Parametros>({
        filtro: {
            // dataInicialDeVencimento: dayjs().startOf("month"),
            // dataFinalDeVencimento: dayjs().endOf("month"),
            tipoDeMovimentacao: "todos",
            apenasComNsu: false,
            apenasComNossoNumeroRetorno : false
        },
        pagina: new PaginaInfo(1, 15),
    });

    const relatorioQuery = useGerarRelatorioDeVisaoGeralDeMovimentacoes_ComPaginacao({ enabled: false, gcTime: 0 }, parametros);

    const onFinish = (_filtro: Filtro) => {
        const novoFiltro: Filtro = {
            ..._filtro,

            dataInicialDeEmissao: _filtro._intervaloDeEmissao?.[0],
            dataFinalDeEmissao: _filtro._intervaloDeEmissao?.[1],
            dataInicialDeVencimento: _filtro._intervaloDeVencimento?.[0],
            dataFinalDeVencimento: _filtro._intervaloDeVencimento?.[1],
            dataInicialDeLiquidacao: _filtro._intervaloDeLiquidacao?.[0],
            dataFinalDeLiquidacao: _filtro._intervaloDeLiquidacao?.[1],

            codCentroCusto: _filtro.centroDeCustos?.codigo,
            codPlanoDeContas: _filtro.planoDeContas?.codigo,
            
            ///@ts-ignore
            codTipoPagamento : _filtro.codTipoPagamento?.codigo
        }

        delete novoFiltro["_intervaloDeEmissao"];
        delete novoFiltro["_intervaloDeVencimento"];
        delete novoFiltro["_intervaloDeLiquidacao"];
        delete novoFiltro["centroDeCustos"];
        delete novoFiltro["planoDeContas"];

        setParametros((prev) => ({ pagina: new PaginaInfo(0, 15), filtro: novoFiltro }));
    };

    useEffect(() => {
        if (parametros != undefined) relatorioQuery.refetch();
    }, [parametros]);

    return (
        <div>
            <Form<Filtro> form={formularioDeFiltro} layout="vertical" size="small" validateTrigger="onBlur" onFinish={onFinish} title="Filtro">
                <Row gutter={8} style={{ marginTop: "1em" }}>
                    <Col>
                        <Button htmlType="submit" className="nao-imprimir" type="primary">
                            Obter relatório
                        </Button>
                    </Col>
                </Row>

                <FiltroComponent />
            </Form>

            <Spin spinning={relatorioQuery.isLoading}>
                <Row gutter={[8, 8]}>
                    <Col xs={24}>
                        {relatorioQuery.data?.data == undefined || relatorioQuery.data.data.registros.length == 0 ? (
                            relatorioQuery.isSuccess ? (
                                <Empty description="Não há dados" style={{ width: "100%" }} />
                            ) : undefined
                        ) : (
                            ((d = relatorioQuery.data.data) => (
                                <React.Fragment>
                                    <Row gutter={[8, 8]}>
                                        <Col xs={24}>
                                            <TabelaDaVisaoGeralDeMovimentacoes
                                                _plus={{
                                                    depoisDeDesativar: () => {
                                                        relatorioQuery.refetch();
                                                    }
                                                }}
                                                tableProps={{
                                                    onChange: (pagination) => {
                                                        setParametros((prev) => ({
                                                            ...prev,
                                                            pagina: new PaginaInfo(pagination.current, pagination.pageSize),
                                                        }));
                                                    },
                                                    dataSource: d,
                                                }}
                                                acquireCSVSource={async () => {
                                                    return (
                                                        await RelatorioService.gerarRelatorioDaVisaoDeTitulos1_Todos(parametros.filtro)
                                                    )?.data;
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                </React.Fragment>
                            ))()
                        )}
                    </Col>
                </Row>
            </Spin>
        </div>
    );
};